import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2 } from "lucide-react"
import { MemberCard } from "./MemberCard"
import { NetworkMembroItem } from "@/types/membros"
import { deleteFavoriteGroup, getFavoriteGroups } from "../services/favoritesService"
import { FavoriteGroup } from "@/types/networking"
import { Button } from "@/components/ui/button"
import { CreateFavoriteGroupModal } from "./CreateFavoriteGroupModal"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useToast } from "@/components/ui/use-toast"
import { motion } from "framer-motion"

export function FavoritesTab() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [openGroupId, setOpenGroupId] = useState<number | null>(null)
  const [favoriteGroups, setFavoriteGroups] = useState<FavoriteGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateModalOpen, setCreateModalOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<FavoriteGroup | null>(null)

  const fetchFavorites = async () => {
    try {
      setLoading(true)
      const groups = await getFavoriteGroups()
      setFavoriteGroups(groups)
    } catch (err) {
      setError("Failed to fetch favorites.")
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchFavorites()
  }, [])

  const toggleGroup = (groupId: number) => {
    setOpenGroupId(openGroupId === groupId ? null : groupId)
  }

  const handleDeleteGroup = async () => {
    if (!groupToDelete) return

    try {
      await deleteFavoriteGroup(groupToDelete.appfav_id)
      toast({
        title: "Sucesso",
        description: "Grupo deletado com sucesso!",
      })
      fetchFavorites()
      setGroupToDelete(null)
    } catch (err) {
      toast({
        title: "Erro",
        description: "Falha ao deletar o grupo.",
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span>Carregando...</span>
      </div>
    )
  }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => setCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Criar Grupo
        </Button>
      </div>
      {favoriteGroups.map((group) => (
        <div
          key={group.appfav_id}
          className="rounded-lg border border-zinc-800"
        >
          <div className="flex w-full items-center justify-between p-4 text-left">
            <button
              onClick={() => toggleGroup(group.appfav_id)}
              className="flex-grow text-left"
            >
              <h3 className="text-lg font-semibold">
                {group.appfav_descricao}{" "}
                <span className="text-orange-500">({group.pessoas.length})</span>
              </h3>
            </button>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setGroupToDelete(group)}
              >
                <Trash2 className="size-4 text-red-500" />
              </Button>
              <button onClick={() => toggleGroup(group.appfav_id)}>
                {openGroupId === group.appfav_id ? (
                  <ChevronUp className="size-5" />
                ) : (
                  <ChevronDown className="size-5" />
                )}
              </button>
            </div>
          </div>

          {openGroupId === group.appfav_id && group.pessoas.length === 0 && (
            <div className="flex items-center justify-center p-4 text-zinc-600">
              Nenhum dado encontrada.
            </div>
          )}
          {openGroupId === group.appfav_id && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="grid grid-cols-1 gap-4 p-4 lg:grid-cols-2 xl:grid-cols-3"
            >
              {group.pessoas.map((member) => (
                <MemberCard
                  key={member.uid}
                  membro={{
                    pesId: 0,
                    pesUid: member.uid,
                    pesNome: member.pes_nome,
                    pesFotoUrl: member.pes_foto_url,
                    empFantasia: member.emp_fantasia,
                    empCidade: member.pes_cidade,
                    empEstado: member.pes_estado_sigla,
                  }}
                  onClick={(uid) => navigate(`/network/member/${uid}`)}
                />
              ))}
            </motion.div>
          )}
        </div>
      ))}
      <CreateFavoriteGroupModal
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onGroupCreated={fetchFavorites}
      />
      <AlertDialog
        open={!!groupToDelete}
        onOpenChange={() => setGroupToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Essa ação não pode ser desfeita. Isso irá deletar permanentemente o grupo{" "}
              <strong>{groupToDelete?.appfav_descricao}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGroup}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
